import { OnboardingProject, OnboardingTask, OnboardingMetrics } from '../components/ControlBridge/OnboardingHub/types';

export interface ClientSummary {
  progress: number;
  targetDate: string;
  nextSteps: { id: string; title: string; due: string }[];
  completed: number;
  remaining: number;
  updates: { ts: string; text: string; by: string }[];
}

export interface ConversionMetrics {
  health: number;
  engagement: { d7: number[]; d30: number[]; d90: number[] };
  dropOff: { color: 'green'|'yellow'|'red'; reasons: string[] };
  ttvDays: number;
  moduleAdoption: Record<string, number>;
}

export interface ResourceHeatmap {
  users: { id: string; name: string; role: string }[];
  weeks: string[];
  load: number[][];
  bottlenecks: { userId: string; reason: string }[];
}

export interface HubTask {
  id: string; 
  title: string; 
  phase: string; 
  estHours: number;
  ownerId: string; 
  due: string; 
  deps: string[]; 
  status: 'todo'|'doing'|'done'|'blocked';
  automations: string[];
  supportTicket?: { id: string; url: string; status: string };
  projectId?: string;
}

const API_BASE = '/api/onboarding';

export const getClientSummary = async (planId: string): Promise<ClientSummary> => {
  try {
    const response = await fetch(`${API_BASE}/projects/${planId}/client-view`);
    if (!response.ok) throw new Error('Failed to fetch client summary');
    return await response.json();
  } catch (error) {
    console.warn('Using mock data for client summary');
    return {
      progress: 72,
      targetDate: '2024-09-30',
      nextSteps: [
        { id: 'task-001', title: 'Import user list', due: '2024-08-30' },
        { id: 'task-002', title: 'Schedule Admin training', due: '2024-09-06' }
      ],
      completed: 18,
      remaining: 7,
      updates: [
        { ts: '2024-08-24T10:14:00Z', text: 'Sample data file passed validation.', by: 'SOMA' },
        { ts: '2024-08-23T16:30:00Z', text: 'Training deck shared.', by: 'PM' }
      ]
    };
  }
};

export const getConversionMetrics = async (planId: string): Promise<ConversionMetrics> => {
  try {
    const response = await fetch(`${API_BASE}/metrics/conversion?projectId=${planId}`);
    if (!response.ok) throw new Error('Failed to fetch conversion metrics');
    return await response.json();
  } catch (error) {
    console.warn('Using mock data for conversion metrics');
    return {
      health: 82,
      engagement: { 
        d7: [45, 52, 48, 61, 55, 58, 62], 
        d30: [48, 52, 55, 58, 61, 59, 62, 65, 63, 67, 70, 68, 72, 75, 73, 76, 78, 80, 77, 81, 83, 85, 82, 86, 88, 90, 87, 91, 93, 95], 
        d90: Array.from({length: 90}, (_, i) => 40 + Math.random() * 60) 
      },
      dropOff: { color: 'green', reasons: [] },
      ttvDays: 45,
      moduleAdoption: {
        'User Management': 95,
        'Data Import': 78,
        'Reporting': 65,
        'Integrations': 42
      }
    };
  }
};

export const getResourceHeatmap = async (planId: string): Promise<ResourceHeatmap> => {
  try {
    const response = await fetch(`${API_BASE}/resources/capacity?projectId=${planId}`);
    if (!response.ok) throw new Error('Failed to fetch resource heatmap');
    return await response.json();
  } catch (error) {
    console.warn('Using mock data for resource heatmap');
    return {
      users: [
        { id: 'user-1', name: 'Sarah Chen', role: 'PM' },
        { id: 'user-2', name: 'Mike Johnson', role: 'Developer' },
        { id: 'user-3', name: 'Lisa Wang', role: 'Designer' },
        { id: 'user-4', name: 'David Rodriguez', role: 'QA' }
      ],
      weeks: ['2024-08-26', '2024-09-02', '2024-09-09', '2024-09-16'],
      load: [
        [85, 90, 95, 80],
        [110, 105, 100, 95],
        [75, 80, 85, 90],
        [60, 65, 70, 75]
      ],
      bottlenecks: [
        { userId: 'user-2', reason: 'Overloaded with critical tasks' }
      ]
    };
  }
};

export const getTasks = async (planId: string): Promise<HubTask[]> => {
  try {
    const response = await fetch(`${API_BASE}/projects/${planId}/tasks`);
    if (!response.ok) throw new Error('Failed to fetch tasks');
    return await response.json();
  } catch (error) {
    console.warn('Using mock data for tasks');
    return [
      {
        id: 'task-001',
        title: 'Import user list',
        phase: 'Data',
        estHours: 8,
        ownerId: 'user-1',
        due: '2024-08-30',
        deps: [],
        status: 'doing',
        automations: ['overdue-reminder'],
        projectId: planId
      },
      {
        id: 'task-002',
        title: 'Configure SSO',
        phase: 'Config',
        estHours: 16,
        ownerId: 'user-2',
        due: '2024-09-08',
        deps: ['task-001'],
        status: 'blocked',
        automations: ['escalation-alert'],
        supportTicket: { id: 'TKT-2024-001', url: '/support/tickets/TKT-2024-001', status: 'open' },
        projectId: planId
      }
    ];
  }
};

export const completeTask = async (planId: string, taskId: string): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE}/projects/${planId}/tasks/${taskId}/complete`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to complete task');
  } catch (error) {
    console.warn('Mock task completion for:', taskId);
  }
};

export const reassignTask = async (planId: string, taskId: string, userId: string): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE}/projects/${planId}/tasks/${taskId}/reassign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    });
    if (!response.ok) throw new Error('Failed to reassign task');
  } catch (error) {
    console.warn('Mock task reassignment for:', taskId, 'to:', userId);
  }
};

export const escalateTask = async (planId: string, taskId: string): Promise<{ id: string; url: string; status: string }> => {
  try {
    const response = await fetch(`${API_BASE}/projects/${planId}/tasks/${taskId}/escalate`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to escalate task');
    return await response.json();
  } catch (error) {
    console.warn('Mock task escalation for:', taskId);
    return { id: 'TKT-2024-002', url: '/support/tickets/TKT-2024-002', status: 'open' };
  }
};

export const sendClientMessage = async (planId: string, data: { text: string; files?: string[] }): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE}/projects/${planId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to send message');
  } catch (error) {
    console.warn('Mock message send for project:', planId, 'message:', data.text);
  }
};
